// src/features/collaboration/components/MessageCenter.js
import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Divider,
  IconButton,
  InputBase,
  Paper,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Message as MessageIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { fetchMessagesForUser, sendMessage, markConversationRead } from '../services/collaborationService';
import { formatDistanceToNow } from 'date-fns';
import { THEME_COLORS } from '../../../styles/theme';
import { useNotifications } from '../../../contexts/NotificationsContext';

const MessageCenter = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const { unreadMessagesCount } = useNotifications();
  
  useEffect(() => {
    const loadMessages = async () => {
      if (!user || !user.id) {
        setLoading(false);
        return;
      }
      
      try {
        const messagesData = await fetchMessagesForUser(user.id);
        setMessages(messagesData);
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadMessages();
  }, [user]);
  
  const unreadCount = messages.filter(msg => !msg.read && msg.recipientId === user?.id).length;
  const displayUnreadCount = Number.isFinite(unreadMessagesCount) ? unreadMessagesCount : unreadCount;
  
  // Format timestamp correctly
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'recently';
    
    // Handle Firestore Timestamp objects
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: false });
  };

  const conversations = useMemo(() => {
    if (!user?.id) return [];
    const map = new Map();
    messages.forEach((msg) => {
      const other = msg.senderId === user.id ? msg.recipient : msg.sender;
      if (!other) return;
      const existing = map.get(other.id);
      if (!existing) {
        map.set(other.id, { other, lastMessage: msg });
      } else {
        const existingTime = existing.lastMessage.createdAt?.seconds || 0;
        const msgTime = msg.createdAt?.seconds || 0;
        if (msgTime > existingTime) {
          map.set(other.id, { other, lastMessage: msg });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const aTime = a.lastMessage.createdAt?.seconds || 0;
      const bTime = b.lastMessage.createdAt?.seconds || 0;
      return bTime - aTime;
    });
  }, [messages, user]);

  const activeThreadMessages = useMemo(() => {
    if (!activeThread || !user?.id) return [];
    const otherId = activeThread.other?.id;
    return messages
      .filter((msg) =>
        (msg.senderId === user.id && msg.recipientId === otherId) ||
        (msg.senderId === otherId && msg.recipientId === user.id)
      )
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return aTime - bTime;
      });
  }, [messages, activeThread, user]);

  useEffect(() => {
    if (!activeThread?.other?.id || !user?.id) return;
    const otherId = activeThread.other.id;
    markConversationRead(user.id, otherId).catch((error) => {
      console.error('Error marking conversation as read:', error);
    });
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.recipientId === user.id && msg.senderId === otherId) {
          return { ...msg, read: true };
        }
        return msg;
      })
    );
  }, [activeThread, user]);
  
  return (
    <Card elevation={2} sx={{ height: '100%', maxHeight: '600px', borderRadius: 2 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              Messages
            </Typography>
            {displayUnreadCount > 0 && (
              <Badge 
                badgeContent={displayUnreadCount} 
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={() => setShowSearch((prev) => !prev)}>
              <SearchIcon />
            </IconButton>
            <IconButton size="small">
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Divider />
        
        {showSearch && (
          <Box sx={{ p: 2 }}>
            <Paper
              component="form"
              sx={{ 
                p: '2px 4px', 
                display: 'flex', 
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                borderRadius: 2
              }}
            >
              <IconButton sx={{ p: '10px' }} aria-label="search">
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder="Search messages"
                inputProps={{ 'aria-label': 'search messages' }}
              />
            </Paper>
          </Box>
        )}
        
        <Divider />
        
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <Typography color="textSecondary">Loading messages...</Typography>
            </Box>
          ) : !user ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              p={4}
              textAlign="center"
            >
              <MessageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                Please sign in to view messages
              </Typography>
            </Box>
          ) : conversations.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              p={4}
              textAlign="center"
            >
              <MessageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="textSecondary">
                No messages yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Connect with educators to start a conversation
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {conversations.map((conversation, index) => (
                <React.Fragment key={conversation.other?.id || `conv-${index}`}>
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      px: 2, 
                      py: 1.5,
                      bgcolor: conversation.lastMessage.read ? 'transparent' : 'rgba(39, 35, 97, 0.05)',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(39, 35, 97, 0.08)'
                      }
                    }}
                    onClick={() => {
                      setActiveThread(conversation);
                      setMessageText('');
                    }}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        color={conversation.other?.online ? 'success' : 'default'}
                      >
                        <Avatar 
                          alt={conversation.other?.name} 
                          src={conversation.other?.profileImage}
                          sx={{ bgcolor: THEME_COLORS.primary }}
                        >
                          {(conversation.other?.name || 'U').charAt(0)}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography 
                            variant="body2" 
                            fontWeight={conversation.lastMessage.read ? 'normal' : 'bold'}
                            noWrap
                          >
                            {conversation.other?.name || 'User'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatTimestamp(conversation.lastMessage.createdAt)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontWeight: conversation.lastMessage.read ? 'normal' : 'medium',
                            color: conversation.lastMessage.read ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {conversation.lastMessage.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < conversations.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, minHeight: 220 }}>
          <Typography variant="subtitle2" color="textSecondary">
            {activeThread?.other?.name || 'Select a conversation'}
          </Typography>

          <Box sx={{ flex: 1, minHeight: 120, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!activeThread ? (
              <Typography color="textSecondary">Choose a connection to view the thread.</Typography>
            ) : activeThreadMessages.length === 0 ? (
              <Typography color="textSecondary">No messages yet.</Typography>
            ) : (
              activeThreadMessages.map((msg) => {
                const isMine = msg.senderId === user?.id;
                return (
                  <Box key={msg.id} display="flex" justifyContent={isMine ? 'flex-end' : 'flex-start'}>
                    <Box
                      sx={{
                        maxWidth: '75%',
                        p: 1.2,
                        borderRadius: 2,
                        bgcolor: isMine ? 'rgba(39, 35, 97, 0.1)' : '#f5f5f5'
                      }}
                    >
                      <Typography variant="body2">{msg.content}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {formatTimestamp(msg.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          <Box display="flex" alignItems="flex-end" gap={1}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder="Write a message..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                    <IconButton
                      color="primary"
                      disabled={sending || !messageText.trim() || !activeThread?.other?.id}
                      sx={{ color: THEME_COLORS.primary }}
                      onClick={async () => {
                        if (!user?.id || !activeThread?.other?.id) return;
                        setSending(true);
                        try {
                          await sendMessage(user.id, activeThread.other.id, messageText.trim());
                          const refreshed = await fetchMessagesForUser(user.id);
                          setMessages(refreshed);
                          setMessageText('');
                        } catch (error) {
                          console.error('Error sending message:', error);
                        } finally {
                          setSending(false);
                        }
                      }}
                      aria-label="send message"
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  pr: 0.5
                },
                '& .MuiInputBase-inputMultiline': {
                  padding: '6px 8px'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MessageCenter;
